import { useState, useMemo } from "react";
import BarraSuperior from "./barra_superior";
import AnimacionCarga from "./animacion_de_carga";
import { consultarSimple } from "../../functions/crud";

const PERMISOS_DISPONIBLES = ["residente", "administrador", "guarda", "super", "empleado"];

const MODULOS_OCULTOS = new Set(["Usuarios", "Clientes", "Rondas", "Tickets", "Otros"]);

const REGEX_MODULOS = [
    { nombre: "Noticias",       test: /^\/?coordi_ph\/\d+\/noticias\// },
    { nombre: "Visitantes",     test: /^\/?coordi_ph\/\d+\/(visitantes|historico|eventos)/ },
    { nombre: "Domicilios",     test: /^\/?coordi_ph\/\d+\/domicilios/ },
    { nombre: "Reservas",       test: /^\/?coordi_ph_clientes\/\d+\/reservas/ },
    { nombre: "Reservas",       test: /^\/?coordi_ph_clientes\/\d+\/reserva_zona/ },
    { nombre: "Reservas",       test: /^\/?coordi_ph_clientes\/\d+\/reserva_mes/ },
    { nombre: "Reservas",       test: /^\/?coordi_ph_clientes\/\d+\/aprobados/ },
    { nombre: "Reservas",       test: /^\/?coordi_ph\/\d+\/pqrs/ },
    { nombre: "Reservas",       test: /^\/?reservas\// },
    { nombre: "Reservas",       test: /^\/?coordi_ph_clientes\/\d+\/.*\/reservas/ },
    { nombre: "Anuncios",       test: /^\/?coordi_ph\/\d+\/anuncios/ },
    { nombre: "Notificaciones", test: /^\/?coordi_ph_clientes\/\d+\/fcm/ },
    { nombre: "Novedades",      test: /^\/?coordi_ph\/\d+\/novedad/ },
    { nombre: "Rondas",         test: /^\/?(?:rondas|rastreo)\// },
    { nombre: "Eventos",        test: /^\/?coordi_ph\/\d+\/eventos/ },
    { nombre: "Tickets",        test: /^\/?tickets\// },
    { nombre: "Usuarios",       test: /^\/?(?:usuarios|autorizados|pendientes|correos)\// },
    { nombre: "Clientes",       test: /^\/?(?:clientes|legal|informe|coordi_ph_clientes\/\d+\/-)/ },
    { nombre: "Censo",          test: /^\/?coordi_ph\/\d+\/censo/ },
    { nombre: "Documentos",     test: /^\/?coordi_ph\/\d+\/documentos/ },
    { nombre: "Chats",          test: /^\/?coordi_ph\/\d+\/chat/ },
    { nombre: "Tutoriales",     test: /^\/?tutoriales\// },
    { nombre: "Noticias",       test: /^\/?(?:calificacion|version|noticias)\// },
    { nombre: "Logs",           test: /^\/?logs\// },
    { nombre: "Domicilios",     test: /^\/?notificaciones(?:_excluidas)?\// },
    { nombre: "SOS",            test: /^\/?sos\// },
    { nombre: "Fiesta",         test: /^\/?fiesta\// },
    { nombre: "Ordenes",        test: /^\/?ordenes\// },
    { nombre: "Notificaciones", test: /^\/?integracion\// },
];

function detectarModulo(url) {
    if (!url) return "Otros";
    for (const { nombre, test } of REGEX_MODULOS) {
        if (test.test(url)) return nombre;
    }
    return "Otros";
}

function categorizarAccion(accion) {
    if (!accion) return null;
    const lower = accion.toLowerCase();
    if (lower.includes("crear") || lower.includes("agregar") || lower.includes("añadir")) return "crear";
    if (lower.includes("leer") || lower.includes("consultar") || lower.includes("ver")) return "leer";
    if (lower.includes("actualizar") || lower.includes("editar") || lower.includes("modificar")) return "actualizar";
    if (lower.includes("eliminar") || lower.includes("borrar") || lower.includes("quitar")) return "eliminar";
    return null;
}

function recalcularConPermisos(clientesBase, mapaUnidades, usuariosPorNitResidente, usuariosPorNitAdministrador, permisosActivos) {
    return clientesBase.map(item => {
        const proyectadas = mapaUnidades.get(item.nombre) || 0;

        const direcciones = new Set();
        if (permisosActivos.includes("residente")) {
            (usuariosPorNitResidente.get(item.nit) || new Set()).forEach(d => direcciones.add(d));
        }
        if (permisosActivos.includes("administrador")) {
            (usuariosPorNitAdministrador.get(item.nit) || new Set()).forEach(d => direcciones.add(d));
        }

        const implementadas = direcciones.size;
        const porcentaje = proyectadas > 0 ? parseFloat(((implementadas / proyectadas) * 100).toFixed(2)) : 0;

        const interacciones = { crear: 0, leer: 0, actualizar: 0, eliminar: 0, total: 0 };

        (item.logsRaw || []).forEach(log => {
            const permisoLog = String(log.permisos || "").toLowerCase().trim();
            if (!permisosActivos.includes(permisoLog)) return;

            const modulo = detectarModulo(log.url);
            if (MODULOS_OCULTOS.has(modulo)) return;

            const cat = categorizarAccion(log.accion);
            if (cat) {
                interacciones[cat]++;
                interacciones.total++;
            }
        });

        return { cliente: item.nombre, nit: item.nit, proyectadas, implementadas, porcentaje, interacciones };
    });
}

const clientesExcluidos = new Set([
    "LABORATORIO",
    "COORDINAR SEGURIDAD Y COMPAÑÍA LTDA",
    "UKUMARI",
    "FUNDACION ECLESIASTICA HOGAR DEL ANCIANO",
    "CENTRO EMPRESARIAL Y COMERCIAL LA VILLA",
    "SOLOMOFLEX S.A.S.",
    "CENTRO LOGÍSTICO CONSOTÁ SAS",
    "CUBA PLAZA S.A.S",
    "POYECTOS DE DESARROLLOS COMERCIAL CG S.A.S",
    "ACC CONSULTORES Y GESTIÓN INMOBILIARIA S.A.S",
    "INVERSIONES RECINTO SAS",
    "SOCIEDAD DE MEJORAS: PARQUEADERO MATECAÑA",
    "CONDOMINIO SAN JOSE DE LAS VILLAS CONJUNTO 3 P.H",
    "EDIFICIO PREMIER 2",
    "QUINTAS DE SANTA BARBARA 7 ETAPA 1",
    "CONJUNTO RESIDENCIAL PLAZUELA DE SAN ESTEBAN P.H",
    "CONJUNTO MULTIFAMILIAR COMPARTIR BOCHICA V ETAPA - PROPIEDAD HORIZONTAL",
    "CONJUNTO RESIDENCIAL VILLAS DE HATO CHICO II PROPIEDAD HORIZONTAL",
    "ALAMEDA DE SAN PATRICIO PH",
    "EDIFICIO TORRES DE SAN RAFAEL",
    "CONJUNTO RESIDENCIAL LA ESTANCIA",
    "CONJUNTO RESIDENCIAL NORMANDIA PARQUE PH",
    "SIERRA VIENTO PH",
    "CONJUNTO DE USO MIXTO CEREZO DE NOVATERRA P.H",
    "ASOCIACIÓN VIVA CERRITOS",
    "LOMALINDA SAS (LA FLORESTA PARQUE RESIDENCIAL)",
    "CONJUNTO 1 BOSQUES DE CUBA - PROPIEDAD HORIZONTAL",
    "CONDOMINIO CAÑAVERDE PROPIEDAD HORIZONTAL",
    "CONJUNTO RESIDENCIAL Y COMERCIAL CAÑAVERAL I - PROPIEDAD HORIZONTAL",
]);

const UnidadesDeViviendaGrafica = () => {
    const [cargando, setCargando] = useState(false);
    const [datosOriginales, setDatosOriginales] = useState(null);
    const [datosOperativosOriginales, setDatosOperativosOriginales] = useState(null);
    const [filtros, setFiltros] = useState({ anio: "", mes: "" });
    const [permisosSeleccionados, setPermisosSeleccionados] = useState(PERMISOS_DISPONIBLES);

    const hoy = useMemo(() => {
        const d = new Date();
        return { año: d.getFullYear(), mes: String(d.getMonth() + 1).padStart(2, "0") };
    }, []);

    const datos = useMemo(() => {
        if (!datosOriginales || permisosSeleccionados.length === 0) return [];
        const { clientesCoordiPH, mapaUnidades, usuariosPorNitResidente, usuariosPorNitAdministrador } = datosOriginales;
        const resultado = recalcularConPermisos(clientesCoordiPH, mapaUnidades, usuariosPorNitResidente, usuariosPorNitAdministrador, permisosSeleccionados);
        resultado.sort((a, b) => b.porcentaje - a.porcentaje);
        return resultado;
    }, [datosOriginales, permisosSeleccionados]);

    const datosOperativos = useMemo(() => {
        if (!datosOperativosOriginales || permisosSeleccionados.length === 0) return [];
        const { clientesOperativos, mapaUnidades, usuariosPorNitResidente, usuariosPorNitAdministrador } = datosOperativosOriginales;
        const resultado = recalcularConPermisos(clientesOperativos, mapaUnidades, usuariosPorNitResidente, usuariosPorNitAdministrador, permisosSeleccionados);
        resultado.sort((a, b) => b.interacciones.total - a.interacciones.total);
        return resultado;
    }, [datosOperativosOriginales, permisosSeleccionados]);

    const togglePermiso = (permiso) => {
        setPermisosSeleccionados(prev =>
            prev.includes(permiso) ? prev.filter(p => p !== permiso) : [...prev, permiso]
        );
    };

    const toggleTodosPermisos = () => {
        setPermisosSeleccionados(prev =>
            prev.length === PERMISOS_DISPONIBLES.length ? [] : [...PERMISOS_DISPONIBLES]
        );
    };

    const consultarLogsMesCompleto = async (nit, anio, mes) => {
        const logsRaw = [];
        try {
            const logsMes = await consultarSimple(`logs/${nit}/${anio}/${mes}`);
            if (!logsMes || typeof logsMes !== "object") return logsRaw;

            Object.values(logsMes).forEach(logsDia => {
                if (!logsDia || typeof logsDia !== "object") return;

                Object.values(logsDia).forEach(logsUser => {
                    if (!logsUser || typeof logsUser !== "object") return;

                    Object.values(logsUser).forEach(log => {
                        if (
                            log &&
                            typeof log === "object" &&
                            !Array.isArray(log) &&
                            typeof log.accion === "string" &&
                            log.accion.trim() !== ""
                        ) {
                            logsRaw.push(log);
                        }
                    });
                });
            });
        } catch (error) {
            console.error(`Error consultando logs para NIT ${nit}:`, error);
        }
        return logsRaw;
    };

    const cargarDatos = async () => {
        if (!filtros.anio || !filtros.mes) {
            alert("Debes seleccionar año y mes para consultar.");
            return;
        }
        setCargando(true);
        try {
            const [clientes, unidades, usuarios] = await Promise.all([
                consultarSimple("clientes"),
                consultarSimple("unidades"),
                consultarSimple("usuarios"),
            ]);

            const unidadesArray = Array.isArray(unidades) ? unidades : Object.values(unidades || {});
            const mapaUnidades = new Map();
            unidadesArray.forEach(item => {
                if (item?.cliente && item?.unidades) {
                    mapaUnidades.set(item.cliente, parseInt(item.unidades) || 0);
                }
            });

            const usuariosPorNitResidente = new Map();
            const usuariosPorNitAdministrador = new Map();

            Object.values(usuarios || {}).forEach(subramas => {
                Object.entries(subramas || {}).forEach(([key, usuario]) => {
                    if (
                        key.startsWith("-") &&
                        usuario?.CodigoCliente &&
                        usuario?.Direccion &&
                        (usuario?.Permisos === "RESIDENTE" || usuario?.Permisos === "ADMINISTRADOR")
                    ) {
                        const nit = usuario.CodigoCliente;
                        const dir = usuario.Direccion.trim().toUpperCase().replace(/\s+/g, " ");
                        if (!dir) return;
                        if (usuario.Permisos === "RESIDENTE") {
                            if (!usuariosPorNitResidente.has(nit)) usuariosPorNitResidente.set(nit, new Set());
                            usuariosPorNitResidente.get(nit).add(dir);
                        } else {
                            if (!usuariosPorNitAdministrador.has(nit)) usuariosPorNitAdministrador.set(nit, new Set());
                            usuariosPorNitAdministrador.get(nit).add(dir);
                        }
                    }
                });
            });

            const promesasClientes = Object.entries(clientes || {}).map(async ([nit, idCliente]) => {
                try {
                    const [estado, nombre] = await Promise.all([
                        consultarSimple(`coordi_ph_clientes/${nit}/${idCliente}/estado`),
                        consultarSimple(`coordi_ph_clientes/${nit}/${idCliente}/Nombre`),
                    ]);
                    return { nit, idCliente, estado, nombre };
                } catch {
                    return null;
                }
            });

            const datosClientes = (await Promise.all(promesasClientes)).filter(Boolean);

            const clientesCoordiPHBase = datosClientes.filter(
                ({ estado, nombre }) => estado === "AUTORIZADO" && nombre
            );
            const clientesOperativosBase = datosClientes.filter(
                ({ estado, nombre }) => estado === "AUTORIZADO" && nombre && clientesExcluidos.has(nombre)
            );

            const enriquecerConLogs = async (clientesBase) => {
                const BATCH_SIZE = 10;
                const resultado = [];
                for (let i = 0; i < clientesBase.length; i += BATCH_SIZE) {
                    const batch = clientesBase.slice(i, i + BATCH_SIZE);
                    const promesas = batch.map(async ({ nit, nombre }) => {
                        const logsRaw = await consultarLogsMesCompleto(nit, filtros.anio, filtros.mes);
                        return { nit, nombre, logsRaw };
                    });
                    resultado.push(...await Promise.all(promesas));
                }
                return resultado;
            };

            const [enrichedCoordiPH, enrichedOperativos] = await Promise.all([
                enriquecerConLogs(clientesCoordiPHBase),
                enriquecerConLogs(clientesOperativosBase),
            ]);

            setDatosOriginales({
                clientesCoordiPH: enrichedCoordiPH,
                mapaUnidades,
                usuariosPorNitResidente,
                usuariosPorNitAdministrador,
            });
            setDatosOperativosOriginales({
                clientesOperativos: enrichedOperativos,
                mapaUnidades,
                usuariosPorNitResidente,
                usuariosPorNitAdministrador,
            });

        } catch (error) {
            console.error("Error cargando datos:", error);
            alert("Ocurrió un error al cargar los datos. Por favor, intenta de nuevo.");
        } finally {
            setCargando(false);
        }
    };

    const calcularPorcentajes = (interacciones) => {
        if (interacciones.total === 0) return { crear: 0, leer: 0, actualizar: 0, eliminar: 0 };
        return {
            crear: parseFloat(((interacciones.crear / interacciones.total) * 100).toFixed(2)),
            leer: parseFloat(((interacciones.leer / interacciones.total) * 100).toFixed(2)),
            actualizar: parseFloat(((interacciones.actualizar / interacciones.total) * 100).toFixed(2)),
            eliminar: parseFloat(((interacciones.eliminar / interacciones.total) * 100).toFixed(2)),
        };
    };

    const getColorPorcentaje = (valor) => {
        if (valor >= 50) return "#4CAF50";
        if (valor >= 20) return "#FF9800";
        return "#F44336";
    };

    const renderFilaDatos = (item, i, prefijo = "") => {
        const porcentajes = calcularPorcentajes(item.interacciones);
        return (
            <tr key={`${prefijo}${item.nit}-${i}`}>
                <td>{item.cliente}</td>
                <td style={{ textAlign: "center" }}>{item.nit}</td>
                <td style={{ textAlign: "center" }}>{item.proyectadas}</td>
                <td style={{ textAlign: "center" }}>{item.implementadas}</td>
                <td style={{ textAlign: "center", fontWeight: "bold", color: item.porcentaje >= 80 ? "#4CAF50" : item.porcentaje >= 50 ? "#FF9800" : "#F44336" }}>
                    {item.porcentaje}%
                </td>
                <td style={{ textAlign: "center" }}>{item.interacciones.total.toLocaleString()}</td>
                <td style={{ textAlign: "center", fontWeight: "bold", color: getColorPorcentaje(porcentajes.crear) }}>{porcentajes.crear}%</td>
                <td style={{ textAlign: "center", fontWeight: "bold", color: getColorPorcentaje(porcentajes.leer) }}>{porcentajes.leer}%</td>
                <td style={{ textAlign: "center", fontWeight: "bold", color: getColorPorcentaje(porcentajes.actualizar) }}>{porcentajes.actualizar}%</td>
                <td style={{ textAlign: "center", fontWeight: "bold", color: getColorPorcentaje(porcentajes.eliminar) }}>{porcentajes.eliminar}%</td>
            </tr>
        );
    };

    const calcularTotalesFooter = (lista) => {
        const totalInteracciones = lista.reduce((sum, item) => sum + item.interacciones.total, 0);
        const calcPct = (tipo) =>
            totalInteracciones > 0
                ? ((lista.reduce((sum, item) => sum + item.interacciones[tipo], 0) / totalInteracciones) * 100).toFixed(2)
                : 0;
        return {
            proyectadas: lista.reduce((sum, item) => sum + item.proyectadas, 0),
            implementadas: lista.reduce((sum, item) => sum + item.implementadas, 0),
            porcentajePromedio: lista.length > 0
                ? (lista.reduce((sum, item) => sum + item.porcentaje, 0) / lista.length).toFixed(2)
                : 0,
            pctCrear: calcPct("crear"),
            pctLeer: calcPct("leer"),
            pctActualizar: calcPct("actualizar"),
            pctEliminar: calcPct("eliminar"),
            totalInteracciones,
        };
    };

    const renderTabla = (lista, titulo, idTabla, prefijo = "") => {
        if (lista.length === 0) return null;
        const totales = calcularTotalesFooter(lista);
        return (
            <div className="BloqueModulo">
                <p>{titulo}</p>
                <br />
                <table id={idTabla} className="tabla-responsive" style={{ width: "100%" }}>
                    <thead>
                        <tr>
                            <th>Cliente</th>
                            <th>NIT</th>
                            <th>Unidades Proyectadas</th>
                            <th>Unidades Implementadas</th>
                            <th>% Implementación</th>
                            <th>Consultas BD</th>
                            <th>% Crear</th>
                            <th>% Leer</th>
                            <th>% Actualizar</th>
                            <th>% Eliminar</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lista.map((item, i) => renderFilaDatos(item, i, prefijo))}
                    </tbody>
                    <tfoot>
                        <tr style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                            <td colSpan="2">TOTALES</td>
                            <td style={{ textAlign: "center" }}>{totales.proyectadas.toLocaleString()}</td>
                            <td style={{ textAlign: "center" }}>{totales.implementadas.toLocaleString()}</td>
                            <td style={{ textAlign: "center" }}>{totales.porcentajePromedio}% (promedio)</td>
                            <td style={{ textAlign: "center", backgroundColor: "#c8e6c9" }}>{totales.totalInteracciones.toLocaleString()}</td>
                            <td style={{ textAlign: "center", backgroundColor: "#e3f2fd" }}>{totales.pctCrear}%</td>
                            <td style={{ textAlign: "center", backgroundColor: "#fff9c4" }}>{totales.pctLeer}%</td>
                            <td style={{ textAlign: "center", backgroundColor: "#ffe0b2" }}>{totales.pctActualizar}%</td>
                            <td style={{ textAlign: "center", backgroundColor: "#ffcdd2" }}>{totales.pctEliminar}%</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        );
    };

    return (
        <div>
            <BarraSuperior />
            <br /><br /><br />

            <div style={{ padding: 20 }}>
                <label>Año: </label>
                <select value={filtros.anio} onChange={(e) => setFiltros({ ...filtros, anio: e.target.value })}>
                    <option value="">-- Seleccionar --</option>
                    {[0, 1, 2].map(i => {
                        const y = hoy.año - i;
                        return <option key={y} value={y}>{y}</option>;
                    })}
                </select>

                <label style={{ marginLeft: 20 }}>Mes: </label>
                <select value={filtros.mes} onChange={(e) => setFiltros({ ...filtros, mes: e.target.value })}>
                    <option value="">-- Seleccionar --</option>
                    {Array.from({ length: 12 }, (_, i) => {
                        const m = String(i + 1).padStart(2, "0");
                        return <option key={m} value={m}>{m}</option>;
                    })}
                </select>

                <button onClick={cargarDatos} style={{ marginLeft: 20, padding: "6px 14px" }} disabled={cargando}>
                    {cargando ? "Consultando..." : "Consultar"}
                </button>
            </div>

            {datosOriginales && (
                <div style={{ padding: 20, backgroundColor: "#f5f5f5", marginBottom: 20 }}>
                    <div style={{ marginBottom: 10 }}>
                        <strong>Filtrar por permisos:</strong>
                        <button
                            onClick={toggleTodosPermisos}
                            style={{ marginLeft: 15, padding: "4px 10px", fontSize: "0.9em" }}
                        >
                            {permisosSeleccionados.length === PERMISOS_DISPONIBLES.length ? "Desmarcar todos" : "Marcar todos"}
                        </button>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "15px" }}>
                        {PERMISOS_DISPONIBLES.map(permiso => (
                            <label key={permiso} style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                                <input
                                    type="checkbox"
                                    checked={permisosSeleccionados.includes(permiso)}
                                    onChange={() => togglePermiso(permiso)}
                                    style={{ marginRight: 6, cursor: "pointer" }}
                                />
                                <span style={{ textTransform: "capitalize" }}>{permiso}</span>
                            </label>
                        ))}
                    </div>
                    <div style={{ marginTop: 10, fontSize: "0.9em", color: "#666" }}>
                        {permisosSeleccionados.length === 0
                            ? "⚠️ Selecciona al menos un permiso para ver los datos"
                            : `Mostrando datos de: ${permisosSeleccionados.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(", ")}`
                        }
                    </div>
                </div>
            )}

            {cargando ? (
                <AnimacionCarga />
            ) : permisosSeleccionados.length === 0 ? (
                <p style={{ padding: 20, textAlign: "center", color: "#999" }}>
                    Selecciona al menos un permiso para visualizar los datos
                </p>
            ) : (
                <>
                    {renderTabla(datos, "Listado de clientes CoordiPH", "table", "")}

                    {datosOperativos.length > 0 && (
                        <>
                            <br /><br />
                            {renderTabla(datosOperativos, "Listado de clientes operativos", "table-operativos", "operativo-")}
                        </>
                    )}

                    {datos.length === 0 && !cargando && (
                        <div className="BloqueModulo">
                            <p style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                                {filtros.anio && filtros.mes
                                    ? "No se encontraron datos para el período seleccionado"
                                    : "Selecciona año y mes para consultar los datos"}
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default UnidadesDeViviendaGrafica;